<template>
  <wizard
    first-step="personalDetails"
    class="wizard-add-child"
  >
    <wizard-step
      :i18n="i18n"
      name="personalDetails"
      :model="formData"
      :next-step="submitPersonalDetails"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.personalDetails.title }}</h2>
        <slot name="personal-details-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <FormulateInput
          name="bonusCode"
          type="text"
          label="bonus code"
          validation="nohtml"
          class="custom-form-element"
          aria-hidden="true"
        />
        <FormulateInput
          name="website"
          type="text"
          label="website"
          validation="nohtml"
          class="custom-form-element"
          aria-hidden="true"
        />
        <FormulateInput
          :label="i18n.form.labels.name"
          name="name"
          validation="bail|required:trim|nohtml"
          :validation-messages="i18n.form.validations.name"
        />

        <FormulateInput
          name="email"
          type="email"
          :label="i18n.form.labels.email"
          validation="bail|required:trim|email"
          :validation-messages="i18n.form.validations.email"
        />

        <FormulateInput
          name="phoneNumber"
          type="text"
          :label="i18n.form.labels.phoneNumber"
          validation="bail|required:trim|nohtml"
          :validation-messages="i18n.form.validations.phoneNumber"
        />

        <FormulateInput
          type="text"
          name="debtorNumber"
          :label="i18n.form.labels.debtorNumber"
          :help="i18n.form.help.debtorNumber"
          validation="bail|required:trim|max:9,length|min:6,length|nohtml|matches:/^[0-9\-]+$/"
          validation-name="Klantnummer"
          :validation-messages="i18n.form.validations.debtorNumber"
          @keydown.space.prevent
        />
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="addChild"
      :model="formData"
      next-step="selectLocation"
      previous-step="personalDetails"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.addChild.title }}</h2>
        <slot name="add-child-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <FormulateInput
          type="text"
          name="childInitials"
          :label="i18n.form.labels.childInitials"
          validation="bail|required:trim|nohtml|matches:/^[A-Z](\.[A-Z])*\.?$/"
          :validation-messages="i18n.form.validations.childInitials"
        />

        <FormulateInput
          :label="i18n.form.labels.childFirstName"
          name="firstName"
          validation="nohtml"
          :validation-messages="i18n.form.validations.childFirstName"
        />

        <FormulateInput
          :label="i18n.form.labels.childNamePrefix"
          name="namePrefix"
          validation="nohtml"
          :validation-messages="i18n.form.validations.childNamePrefix"
        />

        <FormulateInput
          :label="i18n.form.labels.childLastName"
          name="lastName"
          validation="bail|required:trim|nohtml"
          :validation-messages="i18n.form.validations.childLastName"
        />

        <fields-date-picker
          field-name="dateOfBirth"
          date-validation="required"
          :i18n="i18n"
        />

        <fields-gender :i18n="i18n" />

        <FormulateInput
          :label="i18n.form.labels.bsn"
          name="bsn"
          validation="nohtml"
          :validation-messages="i18n.form.validations.bsn"
        />
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="selectLocation"
      :model="formData"
      next-step="selectProducts"
      previous-step="addChild"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.selectLocation.title }}</h2>
        <slot name="select-location-paragraph" />
      </wizard-step-intro>

      <fields-dropdown-cities
        :i18n="i18n"
        :label="label"
        field-name="city"
      />
      <fields-dropdown-city-locations
        :i18n="i18n"
        :label="label"
        field-name="location"
        :is-required="true"
        :city="formData.selectLocation.city"
      />

      <FormulateInput
        :label="i18n.form.labels.schoolName"
        name="schoolName"
        validation="nohtml"
        :validation-messages="i18n.form.validations.schoolName"
      />

      <fieldset />
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="selectProducts"
      :model="formData"
      next-step="review"
      previous-step="selectLocation"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.selectProducts.title }}</h2>
        <slot name="select-products-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <fields-date-picker
          :i18n="i18n"
          :min-date="minDateForChange"
          :date-validation="'bail|required|after:' + dateBeforeMinDateForChange"
        />

        <fieldset>
          <FormulateInput
            name="productTypes"
            :options="{
              preschoolcare: i18n.form.labels.producttypeoptions.preschoolcare,
              afterschoolcare: i18n.form.labels.producttypeoptions.afterschoolcare,
              daycare: i18n.form.labels.producttypeoptions.daycare,
              toddlercare: i18n.form.labels.producttypeoptions.toddlercare,
            }"
            type="checkbox"
            :label="i18n.form.labels.producttype"
            validation="required"
            :validation-messages="i18n.form.validations.producttype"
          />
        </fieldset>

        <fields-product-days
          v-if="isProductTypeSelected('preschoolcare')"
          :i18n="i18n"
          :i18n-description="i18n.form.labels.daypartsvso"
          :i18n-cancellation="i18n.form.labels.cancelvso"
          name-prefix="preschoolCare"
          :show-day-options-as-current-future-cols="false"
        />

        <fields-product-days
          v-if="isProductTypeSelected('afterschoolcare')"
          :i18n="i18n"
          :i18n-description="i18n.form.labels.daysbso"
          :i18n-cancellation="i18n.form.labels.cancelbso"
          name-prefix="afterSchoolCare"
          :show-day-options-as-current-future-cols="false"
        />

        <fields-product-days
          v-if="isProductTypeSelected('daycare')"
          :i18n="i18n"
          :i18n-description="i18n.form.labels.dayskdv"
          :i18n-cancellation="i18n.form.labels.cancelkdv"
          name-prefix="dayCare"
          :show-day-options-as-current-future-cols="false"
        />

        <fields-product-day-parts
          v-if="isProductTypeSelected('toddlercare')"
          :i18n="i18n"
          :i18n-description="i18n.form.labels.dayparts"
          :i18n-cancellation="i18n.form.labels.canceltoddler"
          name-prefix="toddlerCare"
          :show-day-options-as-current-future-cols="false"
        />

        <FormulateInput
          name="remarkYesNo"
          :options="{
            yes: i18n.form.labels.remarkYesNoOptions.yes,
            no: i18n.form.labels.remarkYesNoOptions.no,
          }"
          type="radio"
          :label="i18n.form.labels.remarkYesNo"
          validation="required"
          :validation-messages="i18n.form.validations.remarkYesNo"
        />

        <FormulateInput
          v-if="areThereRemarks"
          name="remark"
          type="textarea"
          validation="nohtml"
          :validation-messages="i18n.form.validations.remark"
        />
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="review"
      :model="formData"
      :next-step="sendRequest"
      previous-step="selectProducts"
      :submit-button-text="i18n.form.buttons.submit"
      :is-submitting="request.isSending"
      :error-submitting="request.errorSending"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.review.title }}</h2>
        <slot name="review-paragraph" />
      </wizard-step-intro>

      <table class="wizard-summary">
        <tbody>
          <tr>
            <th>{{ i18n.form.labels.reviewName }}</th>
            <td>{{ resultingData.personalDetails.name }}</td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewEmail }}</th>
            <td>{{ resultingData.personalDetails.email }}</td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewPhoneNumber }}</th>
            <td>{{ resultingData.personalDetails.phoneNumber }}</td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewCustomerNumber }}</th>
            <td>{{ resultingData.personalDetails.debtorNumber }}</td>
          </tr>
          <tr>
            <td colspan="2">
              {{ '\xa0' }}
            </td>
          </tr>
          <tr>
            <td colspan="2">
              <h3>{{ fullNameOfChild }}</h3>
            </td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.childInitials }}</th>
            <td>{{ resultingData.child.childInitials }}</td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewGender }}</th>
            <td>{{ i18n.form.labels.genderoptions[resultingData.child.gender] }}</td>
          </tr>
          <tr v-if="resultingData.child.bsn">
            <th>{{ i18n.form.labels.reviewSocialSecurityNumber }}</th>
            <td>{{ resultingData.child.bsn }}</td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewDateOfBirth }}</th>
            <td>{{ moment(resultingData.child.dateOfBirth).format('DD-MM-YYYY') }}</td>
          </tr>
          <tr>
            <td colspan="2">
              {{ '\xa0' }}
            </td>
          </tr>
          <tr>
            <td colspan="2">
              <h3>{{ i18n.form.labels.reviewCare }}</h3>
            </td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewDesiredDateOfChange }}</th>
            <td>{{ moment(resultingData.products.date).format('DD-MM-YYYY') }}</td>
          </tr>
          <tr v-if="resultingData.products.preschoolCareDays">
            <th>{{ i18n.form.labels.producttypeoptions.preschoolcare }}</th>
            <td>
              {{
                resultingData.products.preschoolCareDays.map((d) => translateDayPart(d)).join(', ')
              }}
            </td>
          </tr>

          <tr v-if="resultingData.products.afterSchoolCareDays">
            <th>{{ i18n.form.labels.producttypeoptions.afterschoolcare }}</th>
            <td>
              {{
                resultingData.products.afterSchoolCareDays
                  .map((d) => i18n.form.labels.daysoptions[d])
                  .join(', ')
              }}
            </td>
          </tr>
          <tr v-if="resultingData.products.dayCareDays">
            <th>{{ i18n.form.labels.producttypeoptions.daycare }}</th>
            <td>
              {{
                resultingData.products.dayCareDays
                  .map((d) => i18n.form.labels.daysoptions[d])
                  .join(', ')
              }}
            </td>
          </tr>
          <tr v-if="resultingData.products.toddlerCareDayParts">
            <th>{{ i18n.form.labels.producttypeoptions.toddlercare }}</th>
            <td>
              {{
                resultingData.products.toddlerCareDayParts
                  .map((dp) => translateDayPart(dp))
                  .join(', ')
              }}
            </td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewSchoolName }}</th>
            <td>
              {{ resultingData.location.schoolName ? resultingData.location.schoolName : '' }}
            </td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewRemark }}</th>
            <td>
              {{ resultingData.products.remark ? resultingData.products.remark : '' }}
            </td>
          </tr>
        </tbody>
      </table>
    </wizard-step>
  </wizard>
</template>

<script>
import FieldsDatePicker from '@/components/forms/fields/FieldsDatePicker.vue';
import FieldsDropdownCities from '@/components/forms/fields/FieldsDropdownCities.vue';
import FieldsDropdownCityLocations from '@/components/forms/fields/FieldsDropdownCityLocations.vue';
import FieldsGender from '@/components/forms/fields/FieldsGender.vue';
import FieldsProductDayParts from '@/components/forms/fields/FieldsProductDayParts.vue';
import FieldsProductDays from '@/components/forms/fields/FieldsProductDays.vue';
import Wizard from '@/components/wizards/components/Wizard.vue';
import WizardStep from '@/components/wizards/components/WizardStep.vue';
import WizardStepIntro from '@/components/wizards/components/WizardStepIntro.vue';
import getQueryParams from '@/lib/formulate/getFormQueryParam';
import utils from '@/lib/utils';
import axios from 'axios';
import moment from 'moment';

export default {
  name: 'WizardAddChild',
  components: {
    FieldsDatePicker,
    FieldsDropdownCities,
    FieldsDropdownCityLocations,
    FieldsGender,
    FieldsProductDayParts,
    FieldsProductDays,
    Wizard,
    WizardStep,
    WizardStepIntro,
  },
  props: {
    i18n: {
      type: Object,
      default: null,
    },
    prefillData: {
      type: Object,
      default: () => {},
    },
    locale: {
      type: String,
      default: () => 'nl',
    },
    label: {
      type: String,
      default: 'Partou',
    },
    successPageUrl: {
      type: String,
      default: () => '/',
    },
    formToken: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      formData: {
        personalDetails: this.$store.state.wizard.personalDetails,
        addChild: {},
        selectLocation: {},
        selectProducts: {},
      },
      request: {
        isSending: false,
        errorSending: false,
      },
    };
  },
  computed: {
    moment() {
      return moment;
    },
    minDateForChange() {
      return this.formData.addChild?.dateOfBirth
        ? utils.date.getDate(new Date(this.formData.addChild.dateOfBirth), 28)
        : null;
    },
    dateBeforeMinDateForChange() {
      return utils.date.getDate(new Date(this.minDateForChange), -1);
    },
    areThereRemarks() {
      return this.formData.selectProducts.remarkYesNo === 'yes';
    },
    fullNameOfChild() {
      return [
        ...(this.formData.addChild.firstName ? [this.formData.addChild.firstName] : []),
        ...(this.formData.addChild.namePrefix ? [this.formData.addChild.namePrefix] : []),
        ...(this.formData.addChild.lastName ? [this.formData.addChild.lastName] : []),
      ].join(' ');
    },
    resultingData() {
      const selectProducts = this.formData.selectProducts;
      return {
        label: this.label,
        locale: this.locale,
        formToken: this.formToken,
        personalDetails: this.formData.personalDetails,
        child: this.formData.addChild,
        location: this.formData.selectLocation,
        products: {
          date: selectProducts?.date,
          productTypes: selectProducts?.productTypes,
          preschoolCareDays: selectProducts?.preschoolCareFutureDays
            ? selectProducts.preschoolCareFutureDays.map((d) => `${d}morning`)
            : undefined,
          afterSchoolCareDays: selectProducts?.afterSchoolCareFutureDays,
          dayCareDays: selectProducts?.dayCareFutureDays,
          toddlerCareDayParts: selectProducts?.toddlerCareFutureDayParts,
          remark: selectProducts?.remark,
        },
      };
    },
  },
    created() {
        this.formData.personalDetails = {
      ...this.formData.personalDetails,
      ...getQueryParams(this.prefillData)
    }

  },
  methods: {
    isProductTypeSelected(productType) {
      return this.formData.selectProducts?.productTypes?.includes(productType);
    },
    translateDayPart(dayPart) {
      return utils.translate.translateDayPart(dayPart, this.i18n);
    },
    submitPersonalDetails() {
      this.$store.dispatch('wizard/setPersonalDetails', this.formData.personalDetails);
      return 'addChild';
    },
    resetChangesPerChild() {
      Object.keys(this.formData)
        .filter((k) => k.startsWith('changesForChild-'))
        .forEach((k) => (this.formData[k] = {}));
    },
    sendRequest() {
      this.request.errorSending = false;
      this.request.isSending = true;

      const requestVerificationToken = document.body.querySelector('input[type=hidden][name=__RequestVerificationToken]').value;

      const config = {
        headers: {
          'X-REQUEST-VERIFICATION-TOKEN': requestVerificationToken,
        }
      };

      axios
        .post('/umbraco/api/wizard/addchild', this.resultingData, config)
        .then(() => {
          window.location.href = this.successPageUrl;
        })
        .catch(() => {
          this.request.isSending = false;
          this.request.errorSending = true;
        });
    },
  },
};
</script>
